import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";
import { Button, Input, Space, Typography, message } from "antd";
import "./VoiceInput.css";

export type VoiceInputLanguage = "vi-VN" | "ja-JP" | "en-US";

export interface VoiceInputCompletion {
  transcript: string;
  language: VoiceInputLanguage;
}

export interface VoiceInputControls {
  isPanelOpen: boolean;
  isRecording: boolean;
  startRecording: () => Promise<void>;
  stopRecording: () => void;
  cancelRecording: () => void;
}

export interface VoiceInputProps {
  children: (controls: VoiceInputControls) => ReactNode;
  onCompleted: (result: VoiceInputCompletion) => void;
  language?: VoiceInputLanguage;
}

type RecognitionStatus = "idle" | "starting" | "listening" | "stopping";

interface SpeechRecognitionAlternativeLike {
  transcript: string;
}

interface SpeechRecognitionResultLike {
  isFinal: boolean;
  [index: number]: SpeechRecognitionAlternativeLike;
}

interface SpeechRecognitionResultListLike {
  length: number;
  [index: number]: SpeechRecognitionResultLike;
}

interface SpeechRecognitionResultEventLike extends Event {
  resultIndex: number;
  results: SpeechRecognitionResultListLike;
}

interface SpeechRecognitionErrorEventLike extends Event {
  error: string;
}

interface SpeechRecognitionApi extends EventTarget {
  lang: string;
  continuous: boolean;
  interimResults: boolean;

  onstart: (() => void) | null;
  onresult: ((event: SpeechRecognitionResultEventLike) => void) | null;
  onerror: ((event: SpeechRecognitionErrorEventLike) => void) | null;
  onend: (() => void) | null;

  start(): void;
  stop(): void;
  abort(): void;
}

interface SpeechRecognitionConstructor {
  new (): SpeechRecognitionApi;
}

declare global {
  interface Window {
    SpeechRecognition?: SpeechRecognitionConstructor;
    webkitSpeechRecognition?: SpeechRecognitionConstructor;
  }
}

const MAX_RECORDING_DURATION_MS = 30_000;

const getSpeechErrorMessage = (error: string): string => {
  const errorMessages: Record<string, string> = {
    "not-allowed": "マイクの使用が許可されていません。",
    "service-not-allowed":
      "この端末では音声認識サービスを利用できません。",
    "audio-capture": "マイクが見つからないか、使用できません。",
    network:
      "音声認識サービスに接続できません。ネットワークを確認してください。",
    "no-speech": "音声を認識できませんでした。マイクに近づいて話してください。",
    "language-not-supported":
      "現在の認識言語はこのブラウザでサポートされていません。",
  };

  return errorMessages[error] ?? `音声を認識できません: ${error}`;
};

const joinRecognizedParts = (
  parts: string[],
  language: VoiceInputLanguage,
): string => {
  const separator = language === "ja-JP" ? "" : " ";

  return parts
    .map((part) => part.trim())
    .filter(Boolean)
    .join(separator)
    .replace(/\s+([,.!?;:、。！？])/g, "$1")
    .trim();
};

export default function VoiceInput({
  children,
  onCompleted,
  language = "vi-VN",
}: VoiceInputProps) {
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [status, setStatus] = useState<RecognitionStatus>("idle");
  const [transcript, setTranscript] = useState("");
  const [hasInterimResult, setHasInterimResult] = useState(false);

  const recognitionRef = useRef<SpeechRecognitionApi | null>(null);
  const recordingTimeoutRef = useRef<number | undefined>(undefined);
  const recognitionSessionRef = useRef(0);
  const statusRef = useRef<RecognitionStatus>("idle");

  const displayedTranscriptRef = useRef("");
  const finalTranscriptRef = useRef("");
  const interimTranscriptRef = useRef("");

  const isRecording = status !== "idle";

  const setRecognitionStatus = useCallback((nextStatus: RecognitionStatus) => {
    statusRef.current = nextStatus;
    setStatus(nextStatus);
  }, []);

  const clearRecordingTimeout = useCallback(() => {
    if (recordingTimeoutRef.current === undefined) return;

    window.clearTimeout(recordingTimeoutRef.current);
    recordingTimeoutRef.current = undefined;
  }, []);

  const updateDisplayedTranscript = useCallback((value: string) => {
    displayedTranscriptRef.current = value;
    setTranscript(value);
  }, []);

  const resetTranscript = useCallback(() => {
    finalTranscriptRef.current = "";
    interimTranscriptRef.current = "";
    displayedTranscriptRef.current = "";
    setHasInterimResult(false);
    setTranscript("");
  }, []);

  const stopRecording = useCallback(() => {
    const recognition = recognitionRef.current;

    if (!recognition || statusRef.current === "idle") return;

    clearRecordingTimeout();
    setRecognitionStatus("stopping");

    try {
      recognition.stop();
    } catch (error) {
      console.warn("Cannot stop speech recognition", error);

      if (recognitionRef.current === recognition) {
        recognitionRef.current = null;
      }

      setRecognitionStatus("idle");
    }
  }, [clearRecordingTimeout, setRecognitionStatus]);

  const cancelRecording = useCallback(() => {
    const recognition = recognitionRef.current;

    // Vô hiệu hóa callback còn lại của session cũ.
    recognitionSessionRef.current += 1;

    clearRecordingTimeout();
    recognitionRef.current = null;
    setRecognitionStatus("idle");
    resetTranscript();
    setIsPanelOpen(false);

    try {
      recognition?.abort();
    } catch {
      // Recognition có thể đã dừng trước đó.
    }
  }, [clearRecordingTimeout, resetTranscript, setRecognitionStatus]);

  const startRecording = useCallback(async (): Promise<void> => {
    // Dùng ref để chặn double-click trước khi React kịp render lại.
    if (statusRef.current !== "idle" || recognitionRef.current !== null) {
      return;
    }

    const SpeechRecognitionCtor =
      window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognitionCtor) {
      message.error(
        "このブラウザは音声認識に対応していません。対応ブラウザを使用してください。",
      );
      return;
    }

    const session = recognitionSessionRef.current + 1;
    recognitionSessionRef.current = session;

    clearRecordingTimeout();
    resetTranscript();
    setIsPanelOpen(true);
    setRecognitionStatus("starting");

    const recognition = new SpeechRecognitionCtor();
    recognitionRef.current = recognition;

    recognition.lang = language;
    recognition.continuous = true;
    recognition.interimResults = true;

    recognition.onstart = () => {
      if (recognitionSessionRef.current !== session) return;

      setRecognitionStatus("listening");

      recordingTimeoutRef.current = window.setTimeout(() => {
        if (
          recognitionSessionRef.current !== session ||
          recognitionRef.current !== recognition
        ) {
          return;
        }

        setRecognitionStatus("stopping");

        try {
          recognition.stop();
          message.warning("30秒後に録音を自動停止しました。");
        } catch (error) {
          console.warn("Cannot stop timed-out recognition", error);

          if (recognitionRef.current === recognition) {
            recognitionRef.current = null;
          }

          setRecognitionStatus("idle");
        }
      }, MAX_RECORDING_DURATION_MS);
    };

    const normalizeForCompare = (value: string) =>
      value.trim().replace(/\s+/g, " ").toLocaleLowerCase();

    const mergeRecognizedParts = (
      parts: string[],
      language: VoiceInputLanguage,
    ) => {
      return parts.reduce((mergedText, rawPart) => {
        const nextText = rawPart.trim();

        if (!nextText) return mergedText;
        if (!mergedText) return nextText;

        const mergedNormalized = normalizeForCompare(mergedText);
        const nextNormalized = normalizeForCompare(nextText);

        // Browser trả bản nháp chứa cả nội dung cũ, ví dụ:
        // "alo" -> "alo 1" -> "alo 1 2"
        // thì thay bằng bản mới, không nối thêm.
        if (nextNormalized.startsWith(mergedNormalized)) {
          return nextText;
        }

        return joinRecognizedParts([mergedText, nextText], language);
      }, "");
    };

    recognition.onresult = (event) => {
      if (recognitionSessionRef.current !== session) return;

      const finalParts: string[] = [];
      let latestInterimText = "";

      for (let index = 0; index < event.results.length; index += 1) {
        const result = event.results[index];
        const text = result?.[0]?.transcript ?? "";

        if (result.isFinal) {
          finalParts.push(text);
        } else {
          // Chỉ giữ interim mới nhất, không nối tất cả interim lại.
          latestInterimText = text;
        }
      }

      const finalText = mergeRecognizedParts(finalParts, language);
      const displayedText = mergeRecognizedParts(
        [finalText, latestInterimText],
        language,
      );

      finalTranscriptRef.current = finalText;
      interimTranscriptRef.current = latestInterimText;

      setHasInterimResult(Boolean(latestInterimText));
      updateDisplayedTranscript(displayedText);
    };

    recognition.onerror = (event) => {
      if (recognitionSessionRef.current !== session) return;

      if (event.error !== "aborted") {
        console.error("Speech recognition failed", event.error);
        message.error(getSpeechErrorMessage(event.error));
      }

      clearRecordingTimeout();

      if (recognitionRef.current === recognition) {
        recognitionRef.current = null;
      }

      setRecognitionStatus("idle");
    };

    recognition.onend = () => {
      if (recognitionSessionRef.current !== session) return;

      clearRecordingTimeout();

      if (recognitionRef.current === recognition) {
        recognitionRef.current = null;
      }

      setRecognitionStatus("idle");

      const finalText = finalTranscriptRef.current.trim();
      const interimText = interimTranscriptRef.current.trim();
      const displayedText = displayedTranscriptRef.current.trim();

      // Nếu câu cuối vẫn là interim thì giữ lại để người dùng kiểm tra,
      // tránh làm mất phần vừa nói.
      const textToKeep = interimText
        ? displayedText
        : finalText || displayedText;

      setHasInterimResult(Boolean(interimText));
      updateDisplayedTranscript(textToKeep);
    };

    try {
      // Phải được gọi trực tiếp từ click của người dùng.
      recognition.start();
    } catch (error) {
      console.error("Cannot start speech recognition", error);

      if (recognitionSessionRef.current === session) {
        recognitionSessionRef.current += 1;
        clearRecordingTimeout();
        recognitionRef.current = null;
        setRecognitionStatus("idle");
        setIsPanelOpen(false);
        resetTranscript();
      }

      message.error("音声認識を開始できません。");
    }
  }, [
    clearRecordingTimeout,
    language,
    resetTranscript,
    setRecognitionStatus,
    updateDisplayedTranscript,
  ]);

  const confirmAndSave = () => {
    if (isRecording) {
      message.warning("確認する前に録音を停止してください。");
      return;
    }

    const completedTranscript = transcript.trim();

    if (!completedTranscript) {
      message.warning("音声を認識できませんでした。");
      return;
    }

    onCompleted({
      transcript: completedTranscript,
      language,
    });

    resetTranscript();
    setIsPanelOpen(false);
  };

  useEffect(() => {
    return () => {
      recognitionSessionRef.current += 1;
      clearRecordingTimeout();

      const recognition = recognitionRef.current;
      recognitionRef.current = null;

      try {
        recognition?.abort();
      } catch {
        // Recognition đã kết thúc.
      }
    };
  }, [clearRecordingTimeout]);

  return (
    <>
      {children({
        isPanelOpen,
        isRecording,
        startRecording,
        stopRecording,
        cancelRecording,
      })}

      {isPanelOpen && (
        <section className="voice-input-panel" aria-label="音声入力">
          <Space direction="vertical" size="middle" style={{ width: "100%" }}>
            <div aria-live="polite">
              {status === "starting" && (
                <Typography.Text type="secondary">
                  マイクを起動しています…
                </Typography.Text>
              )}

              {status === "listening" && (
                <Typography.Text type="danger">録音中…</Typography.Text>
              )}

              {status === "stopping" && (
                <Typography.Text type="secondary">
                  最後の発話を処理しています…
                </Typography.Text>
              )}

              {status === "idle" && hasInterimResult && (
                <Typography.Text type="warning">
                  最後の部分が認識途中の可能性があります。確認前に内容を確認してください。
                </Typography.Text>
              )}
            </div>

            <Input.TextArea
              value={transcript}
              readOnly={isRecording}
              onChange={(event) => {
                const value = event.target.value;
                setHasInterimResult(false);
                updateDisplayedTranscript(value);
              }}
              rows={5}
              placeholder="音声から認識した内容がここに表示されます..."
              aria-label="音声認識の内容"
            />

            <Space>
              {isRecording && (
                <Button
                  onClick={stopRecording}
                  disabled={status === "stopping"}
                >
                  録音を停止
                </Button>
              )}

              <Button
                type="primary"
                icon={<CheckOutlined />}
                disabled={isRecording || !transcript.trim()}
                onClick={confirmAndSave}
              >
                確認
              </Button>

              <Button icon={<CloseOutlined />} onClick={cancelRecording}>
                キャンセル
              </Button>
            </Space>
          </Space>
        </section>
      )}
    </>
  );
}
